import { useState, useEffect, useMemo } from "react";
import { FormGroup, Label, Input, Col, Row } from "reactstrap";
import { FormattedMessage } from "react-intl";
import { injectIntl } from "react-intl";
import Select from "react-select";
import axios from "../../services/api/api";

const CreateNewScormModuleForm = ({
  setModule,
  module,
  errors,
  setErrors,
  moduleToEdit,
  intl,
  gy25Modules,
}) => {
  const [qApiIdCheckResult, setQApiIdCheckResult] = useState({});
  const handleSetName = (name) => {
    setErrors(errors.filter((obj) => obj !== "name"));
    setModule({
      ...module,
      name: name,
    });
  };

  const handleSetDescription = (description) => {
    setErrors(errors.filter((obj) => obj !== "description"));
    setModule({
      ...module,
      description: description,
    });
  };

  const factModuleDisabled = useMemo(
    () => (module.maxScoreIsSetByModule && !module.isFinalExam ? false : true),
    [module.isFinalExam, module.maxScoreIsSetByModule]
  );

  const qApiIdDisabled = useMemo(
    () =>
      !module.maxScoreIsSetByModule ||
      module.isFactModule ||
      module.isFinalExam,
    [module.isFactModule, module.isFinalExam, module.maxScoreIsSetByModule]
  );

  const maxActivityPointsDisabled = useMemo(
    () =>
      (!module.isFinalExam &&
        !module.isFactModule &&
        module.maxScoreIsSetByModule) ||
      (module.isFinalExam && module.maxScoreIsSetByModule),
    [module.isFactModule, module.isFinalExam, module.maxScoreIsSetByModule]
  );

  const matchedGy25Module = useMemo(
    () =>
      gy25Modules.find(
        (gy25m) =>
          gy25m.moduleCoupleId !== null &&
          gy25m.moduleCoupleId === module.moduleCoupleId &&
          gy25m.isFactModule !== module.isFactModule
      ),
    [gy25Modules, module.isFactModule, module.moduleCoupleId]
  );

  const handleCheckQApiId = (qApiId) => {
    axios
      .get(`/Module/CheckQApiId?qApiId=${encodeURIComponent(qApiId)}`)
      .then((response) => {
        if (response.data && response.data.result) {
          setQApiIdCheckResult(response.data.result);
        } else {
          setQApiIdCheckResult({});
        }
      })
      .catch(() => {
        setQApiIdCheckResult({});
      });
  };

  const handleSetQApiId = (qApiId) => {
    if (qApiId.trim() === "") {
      setQApiIdCheckResult({});
      setModule((prev) => ({ ...prev, qApiId: "" }));
      return;
    }
    if (qApiId.length > 0) {
      setErrors((prev) => prev.filter((obj) => obj !== "qApiId"));
    }
    if (!qApiIdDisabled) {
      handleCheckQApiId(qApiId.trim());
    }
    setModule((prev) => ({ ...prev, qApiId }));
  };

  const handleSetFile = (file) => {
    setErrors(errors.filter((obj) => obj !== "file"));
    setModule({ ...module, file: file });
  };

  const handleSetGroupId = (selected) => {
    //Remove
    const highestModuleCoupleId = Math.max(
      ...gy25Modules.map((m) => m.moduleCoupleId).filter((id) => id !== null),
      0
    );
    const newModuleCoupleId = highestModuleCoupleId + 1;
    if (selected.value === "remove") {
      setModule({ ...module, moduleCoupleId: null });
      if (
        gy25Modules.some(
          (m) =>
            m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id
        )
      ) {
        const affectedModule = gy25Modules.find(
          (m) =>
            m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id
        );
        affectedModule.moduleCoupleId = null;
        setModule({
          ...module,
          moduleCoupleId: null,
          affectedModules: [affectedModule],
        });
      } else {
        setModule({ ...module, moduleCoupleId: null });
      }
      return;
    }
    //Gör bara om koppling är ny
    if (
      module.moduleCoupleId !== selected.value.moduleCoupleId ||
      module.moduleCoupleId == null
    ) {
      if (module.moduleCoupleId == null) {
        //1 Modul har ingen koppling
        if (selected.value.moduleCoupleId == null) {
          //1.1 Modul har ingen koppling och vald modul har ingen koppling
          const affectedModule = selected.value;
          affectedModule.moduleCoupleId = newModuleCoupleId;
          setModule({
            ...module,
            moduleCoupleId: newModuleCoupleId,
            affectedModules: [affectedModule],
          });
        } else {
          //1.2 Modul har ingen koppling men vald modul har koppling
          if (
            gy25Modules.some(
              (m) =>
                m.moduleCoupleId === selected.value.moduleCoupleId &&
                m.id !== selected.value.id
            )
          ) {
            const moduleToDisconnect = gy25Modules.find(
              (m) =>
                m.moduleCoupleId === selected.value.moduleCoupleId &&
                m.id !== selected.value.id
            );
            moduleToDisconnect.moduleCoupleId = null;
            const moduleToConnect = selected.value;
            moduleToConnect.moduleCoupleId = newModuleCoupleId;
            setModule({
              ...module,
              moduleCoupleId: newModuleCoupleId,
              affectedModules: [moduleToDisconnect, moduleToConnect],
            });
          }
        }
      } else {
        //2 Modul har koppling
        if (selected.value.moduleCoupleId == null) {
          //2.1 Modul har koppling men vald modul har ingen koppling
          const moduleToDisconnect = gy25Modules.find(
            (m) =>
              m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id
          );
          if (moduleToDisconnect != null) {
            moduleToDisconnect.moduleCoupleId = null;
          }
          const moduleToUpdate = selected.value;
          moduleToUpdate.moduleCoupleId = newModuleCoupleId;
          setModule({
            ...module,
            moduleCoupleId: newModuleCoupleId,
            affectedModules: [
              moduleToDisconnect ?? moduleToDisconnect,
              moduleToUpdate,
            ],
          });
        } else {
          //2.2 Modul har koppling och vald modul har koppling
          const moduleToDisconnect = gy25Modules.find(
            (m) =>
              m.moduleCoupleId === module.moduleCoupleId && m.id !== module.id
          );
          const moduleToDisconnectFromSelected = gy25Modules.find(
            (m) =>
              m.moduleCoupleId === selected.value.moduleCoupleId &&
              m.id !== selected.value.id
          );
          if (moduleToDisconnect != null) {
            moduleToDisconnect.moduleCoupleId = null;
          }
          if (moduleToDisconnectFromSelected != null) {
            moduleToDisconnectFromSelected.moduleCoupleId = null;
          }
          const moduleToConnect = selected.value;
          moduleToConnect.moduleCoupleId = newModuleCoupleId;
          setModule({
            ...module,
            moduleCoupleId: newModuleCoupleId,
            affectedModules: [
              moduleToDisconnect ?? moduleToDisconnect,
              moduleToDisconnectFromSelected ?? moduleToDisconnectFromSelected,
              moduleToConnect,
            ],
          });
        }
      }
    }
  };
  const handleSetCoupleOptions = () => {
    let retList = [
      ...gy25Modules
        .filter((m) => m.isFactModule !== module.isFactModule)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((option) => ({
          ...option,
          label:
            option.moduleCoupleId !== null &&
            gy25Modules.some(
              (m) =>
                m.moduleCoupleId === option.moduleCoupleId && m.id !== option.id
            )
              ? option.name +
                " - för närvarande kopplad till: " +
                gy25Modules.find(
                  (m) =>
                    m.moduleCoupleId === option.moduleCoupleId &&
                    m.id !== option.id
                ).name
              : option.name +
                " - " +
                (option.url.includes("Scormfiles/")
                  ? option.url
                      .split("Scormfiles/")[1]
                      .split("/index_lms.html")[0]
                  : option.url),
          value: option,
          clearableValue: true,
        })),
    ];
    const removeOption = {
      label: "➖ Ta bort koppling",
      value: "remove",
    };
    if (moduleToEdit != null) {
      retList = [removeOption, ...retList];
    }
    return retList;
  };

  useEffect(() => {
    if (qApiIdDisabled) {
      setQApiIdCheckResult({});
      setErrors((prev) => prev.filter((obj) => obj !== "qApiId"));
      setModule((prev) => ({ ...prev, qApiId: "" }));
      return;
    }
  }, [qApiIdDisabled, setErrors, setModule]);

  useEffect(() => {
    if (qApiIdDisabled) return;

    if (qApiIdCheckResult.exists === false) {
      setErrors((prev) =>
        prev.includes("qApiId") ? prev : [...prev, "qApiId"]
      );
    } else if (qApiIdCheckResult.exists === true) {
      setErrors((prev) => prev.filter((obj) => obj !== "qApiId"));
    }
  }, [qApiIdDisabled, qApiIdCheckResult.exists, setErrors]);

  return (
    <FormGroup>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="File">Scorm-zip</Label>
        </Col>
        <Col>
          <Input
            type="file"
            onChange={(e) => handleSetFile(e.target.files[0])}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="Answers">Facit-pdf</Label>
        </Col>
        <Col>
          <Input
            type="file"
            onChange={(e) =>
              setModule({ ...module, answers: e.target.files[0] })
            }
          />
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="Name" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="Name" />
          </Label>
        </Col>
        <Col>
          <Input
            name="Name"
            type="text"
            className={errors.includes("name") ? "input--withWarning" : ""}
            value={module.name}
            onChange={(data) => handleSetName(data.target.value)}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="Description" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="Description" />
          </Label>
        </Col>
        <Col>
          <Input
            name="Description"
            type="textarea"
            className={
              errors.includes("description") ? "input--withWarning" : ""
            }
            value={module.description}
            onChange={(data) => handleSetDescription(data.target.value)}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="ApproveScoreLimit" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="ApproveScoreLimitInPercentage" />
          </Label>
        </Col>
        <Col>
          <Input
            disabled={moduleToEdit != null}
            title={
              moduleToEdit != null
                ? intl.formatMessage({
                    id: "ContactAdministrator",
                  })
                : ""
            }
            name="ApproveScoreLimit"
            type="number"
            value={module.approveScoreLimit}
            onChange={(data) =>
              setModule({
                ...module,
                approveScoreLimit: data.target.value,
              })
            }
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="MaxScore" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="MaxScore" />
          </Label>
        </Col>
        <Col>
          <Input
            name="MaxScore"
            type="number"
            value={!module.maxScoreIsSetByModule ? module.maxScore : ""}
            onChange={(data) =>
              setModule({
                ...module,
                maxScore: data.target.value,
              })
            }
            disabled={module.maxScoreIsSetByModule ? true : false}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="MaxActivityPoints" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="MaxActivityPoints" />
          </Label>
        </Col>
        <Col>
          <Input
            name="MaxActivityPoints"
            type="number"
            value={!maxActivityPointsDisabled ? module.maxActivityPoints : ""}
            onChange={(data) => {
              setModule({
                ...module,
                maxActivityPoints: data.target.value,
              });
            }}
            disabled={maxActivityPointsDisabled}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="IsFinalExam" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="IsFinalExam" />
          </Label>
        </Col>
        <Col>
          <Input
            style={{ marginLeft: "0", marginRight: "1rem", marginTop: "1rem" }}
            name="Name"
            type="checkbox"
            checked={module.isFinalExam}
            onChange={(data) =>
              setModule({ ...module, isFinalExam: data.target.checked })
            }
            disabled={module.isFactModule}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="MaxScoreIsSetByModule" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="MaxScoreIsSetByModule" />
          </Label>
        </Col>
        <Col>
          <Input
            style={{ marginLeft: "0", marginRight: "1rem", marginTop: "1rem" }}
            name="MaxScoreIsSetByModule"
            type="checkbox"
            checked={module.maxScoreIsSetByModule}
            onChange={(data) =>
              setModule({
                ...module,
                maxScoreIsSetByModule: data.target.checked,
              })
            }
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="IsFactModule" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="FactModule" />
          </Label>
        </Col>
        <Col>
          <Input
            style={{ marginLeft: "0", marginRight: "1rem", marginTop: "1rem" }}
            name="IsFactModule"
            type="checkbox"
            checked={factModuleDisabled ? false : module.isFactModule}
            onChange={(data) =>
              setModule({
                ...module,
                qApiId: data.target.checked ? "" : module.qApiId,
                isFactModule: data.target.checked,
              })
            }
            disabled={factModuleDisabled}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="QApiId" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="QApiId" />
          </Label>
        </Col>
        <Col>
          <Input
            name="QApiId"
            className={
              errors.includes("qApiId") && !qApiIdDisabled
                ? "input--withWarning"
                : ""
            }
            type="text"
            maxLength={21}
            value={module.qApiId}
            onChange={(data) => handleSetQApiId(data.target.value)}
            disabled={qApiIdDisabled}
          ></Input>
          {qApiIdCheckResult.hasQApiFetchError ? (
            <div className="text-danger input-feedback">
              <FormattedMessage id="CouldNotValidateQApiId" />
            </div>
          ) : qApiIdCheckResult.exists === false ? (
            <div className="text-danger input-feedback">
              <FormattedMessage id="QApiIdNotExists" />
            </div>
          ) : qApiIdCheckResult.exists === true ? (
            <div className="text-success input-feedback">
              {qApiIdCheckResult.qApiId} - {qApiIdCheckResult.name}
            </div>
          ) : null}
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="CoupleId" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="CoupleId" />
          </Label>
        </Col>
        <Col>
          <Input
            name="CoupleId"
            type="number"
            value={module.moduleCoupleId}
            disabled={true}
          ></Input>
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg="4">
          <Label for="Couple" style={{ marginTop: ".5rem" }}>
            <FormattedMessage id="ConnectedTo" />
          </Label>
        </Col>
        <Col>
          <Select
            placeholder="Välj modul att para med"
            defaultValue={
              matchedGy25Module
                ? { value: matchedGy25Module, label: matchedGy25Module.name }
                : ""
            }
            noOptionsMessage={() => "Det finns inga program att välja"}
            name="Couple"
            onChange={(selected) => selected && handleSetGroupId(selected)}
            options={handleSetCoupleOptions()}
            isDisabled={module.isFinalExam || !module.maxScoreIsSetByModule}
            isClearable
          />
        </Col>
      </Row>
    </FormGroup>
  );
};
export default injectIntl(CreateNewScormModuleForm);
